import { actions } from "Js/actionsProcessor";
import router from "./routes.js";
import store from "./store";

// Default behaviors for common HTTP and custom error codes
const defaultBehaviours = [
    // Handling Unauthorized Access
    {
        'codes': ['401'],
        'function': function() {
            router.push('/login?redirect=' + encodeURIComponent(router.currentRoute.fullPath));
        }
    },
    // Handling Client and Server Errors
    {
        'codes': [
            // List of HTTP error codes like 400, 404, etc.
            '400', '402', '403', '404', '405', '406', '407', '408', '409', '410',
            '411', '412', '413', '414', '415', '416', '417', '418', '421', '422',
            '423', '424', '425', '426', '428', '429', '431', '451',
            // Custom error codes specific to FileMaker Server
            '212', '802', '952', '953', '959', '1701', '1711', '1713'
        ],
        'function': function(error) {
            let code = typeof error[0] === 'string' ? error[0] : error[0].toString(),
                desc = '';
            // Describing the error based on the code
            switch (code) {
                // Standard HTTP error descriptions
                case '403':
                    desc = 'Forbidden';
                    break;
                case '404':
                    desc = 'Page Not Found';
                    break;
                    // ...
                    // Custom descriptions for FileMaker Server errors
                case '212':
                case '802':
                    // ...
                    desc = 'FileMaker Server Error';
                    break;
                default:
                    desc = 'Bad Request';
            }
            redirectErrorPage({ code: code, message: error[1], description: desc });
        }
    },
    // Logging Errors to Console
    {
        'codes': ['959', '10050', '10110'],
        'function': function(error) {
            console.error(`Error (${error[0]}): ${error[1]} - ${error[2].action}`);
        }
    },
    // Handling Network and Server Errors
    {
        'codes': ['ECONNABORTED', '3', '821', '1630', '10050', '10901', '10903'],
        'function': function(error) {
            // Additional handling for specific error codes
            if (error[0] == 3) { error[1] += ' (Check if DAPI gateway is enabled on FMS)' }
            /\r|\n/.exec(error[2]) ? error[2] = 'Server Error' : null;
            window.location.href = `http://error.fmbetterforms.com?code=${error[0]}&description=${error[1]}&type=${error[2]}`;
        }
    },
    // Logging Non-Fatal Errors to Console
    {
        'codes': ['10130', '10200', '10150', '10151', '10152', '10153', '10154', '10155', '10156', '10157', '10158', '10160', '10170'],
        'function': function(error) {
            console.error(error[1]);
        }
    },
    // Handling Authentication Errors
    {
        'codes': ['10150', '10151', '10152', '10153', '10154', '10155', '10156', '10160'],
        'function': function(error) {
            Vue.set(formGen.formSchema.model, "authMessage", error[1]);
        }
    },
    // Processing Non-Fatal Errors
    {
        'codes': ['10110', '10112', '10141', '10150', '10151', '10152', '10153', '10154', '10155', '10156', '10157', '10158', '10160', '10170'],
        'function': function(error) {
            var pendingThreads = store.getters.pendingThreads;
            if (pendingThreads.length > 0) {
                pendingThreads.forEach(idThread => {
                    window.vueapp.$nextTick(actions.process(idThread));
                });
                store.commit('CLEAR_PENDINGTHREADS');
            }
            else {
                window.vueapp.$nextTick(actions.process());
            }
        }
    },
    // Alerting User for Specific Errors
    {
        'codes': ['9', '10112', '10130', '10131', '10140', '10157', '10300', '10200', '10201', '10202', '10203'],
        'function': function(error) {
            alert(`Error ${error[0]}: ${error[1]}`);
        }
    },
    // Handling Logout for Specific Errors
    {
        'codes': ['10157', '10158'],
        'function': function() {
            store.dispatch("LOGOUT");
        }
    }
];

// List of error codes that do not require action clearing
const nonBlockingErrors = [401, 10021, 10110, 10112, 10141, 10150, 10151, 10152, 10153, 10154, 10155, 10156, 10157, 10158];

// Function to clear actions based on error code
function clearActions(errorCode) {
    if (!nonBlockingErrors.includes(errorCode) && errorCode !== undefined) {
        store.dispatch('ACTIONS_CLEAR');
    }
}

// Default error handling
function handleDefault(error) {
    try {
        defaultBehaviours.forEach(item => {
            if (item.codes.includes(error[0].toString())) {
                item.function(error);
            }
        });
    }
    catch (error) {
        return;
    }
}

// Redirecting to error page
function redirectErrorPage(error) {
    if (!window.location.href.match(/.*\/#\/error$/)) {
        router.push('/error');
        store.state.site.content.app.error = error;
    }
}

// Splitting custom error keys for handling
function splitCustomErrorKeys() {
    var customHandlers = store.state.site.content.app.errorHandlers || [],
        newObj = {};

    customHandlers.forEach(item => {
        item.codes.forEach(code => {
            newObj[code] = Array.isArray(item.actions) ? item.actions : [item.actions];
        });
    });
    return newObj;
}

// Handling custom error handlers
function handleCustomErrorHandler(handler, error) {
    handler.forEach(item => {
        Object.assign(item.options, { error: { code: error[0], message: error[1], description: error[2] } });
        actions.dispatchAction(item);
    });
}

// Main action handler for errors
function handleAction() {
    var errorCode = arguments[0],
        customErrorHandlers = splitCustomErrorKeys();
    store.dispatch('ERROR_PUSH', { code: arguments[0], message: arguments[1], info: arguments[2] });

    if (customErrorHandlers[errorCode]) {
        handleCustomErrorHandler(customErrorHandlers[errorCode], arguments);
    }
    else {
        console.error(`${arguments[0] ? 'Error ' + arguments[0] : 'Error'}: ${arguments[1]}\n${typeof arguments[2] === 'object' ? JSON.stringify(arguments[2], null, 2) : arguments[2]}`);
        handleDefault(arguments);
    }
    clearActions(errorCode);
}

// Main error handler export
const errorHandler = function() { 
    const args = arguments[0];
    if (args.length === 1) {
        handleAction(args[0].code, args[0].message, args[0].stack);
    }
    else if (args.length === 3) {
        handleAction(args[0], args[1], args[2]);
    }
};

export { errorHandler };
